import { wallOpeningSide, wallOpeningSideLayer } from "building-common/openings/openingQuery";
import { getSubMassParentTag } from "building-types/buildingGeomTag/buildingGeomTags";
import type { BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import { TAG_POSITION_BOTTOM, TAG_POSITION_TOP } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { OpeningSide, OpeningSideName } from "building-types/openings/openingTypes";
import { BACK, FRONT } from "building-types/openings/openingTypes";
import { OpeningWrappingModeEnum } from "building-types/openings/openingWrappingModes";
import type { WallCut } from "building-types/walls/wallCutTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallFaces } from "building-types/walls/wallFacesTypes";
import type { WallSideContext } from "building-types/walls/wallSideTypes";
import type { SolidWallSide } from "building-types/walls/wallTypes";
import type { SolidWallMass, WallMass } from "building-types/walls/wallTypes";
import { WallLOD } from "building-types/walls/wallTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { levelInchesNew } from "common/utils/nominalNumbers";
import shallowCompare from "common/utils/shallowCompare";
import { isFinishLayer } from "doc/utils/wallAssemblyTools";
import type { Face } from "geom/brep";
import { faceNew, faceReverse, mergeNonAdjacentCoplanarFaces, tagFace } from "geom/brep";
import { DIST_EPSILON, ZERO_EPSILON } from "geom/core";
import { planeCopy, planeNewXY } from "geom/plane";
import { polyLine2Area, polyLine2Copy, polyLine2SetCW } from "geom/polyLine2";
import { solidWallMakeOpeningHoleFinishFaces } from "./holeFaces";
import { iterableSolidWallSideOpenings } from "./iterableSolidWallSideOpenings";
import { performWallCut } from "./wallCut";
import { mergeWallFaces, wallFacesFromWallMass, wallFacesNew, wallFacesOpposite } from "./wallFaces";
import { wallMassCrossSectionLayer, wallMassLayers } from "./wallLayers";
import { gatherOpeningDataFromMassOpenings, unionedWallSideOpenings, unionMassOpenings } from "./wallOpeningUnions";
import { wallSideFinishMassCrossSection } from "./wallSideFinishMassCrossSection";
import { isSolidWallMass } from "./wallTypeGuards";

export function makeSolidWallFaces({
  mass,
  leftSide,
  rightSide,
}: {
  mass: WallMass;
  leftSide: SolidWallSide;
  rightSide: SolidWallSide;
}): { wallFaces: WallFaces; wallOutlineFaces: WallFaces } {
  const wallFaces = wallFacesNew();
  const wallOutlineFaces = wallFacesNew();

  // Add top/bottom faces not created by side finish layers
  wallFacesAddCoreFaces(wallFaces, mass, leftSide, rightSide);

  /**
   * gather all of the openings for this mass
   * @todo(Andrew): we should store the actual WallOpeningSide, rather than the
   * Door|Window|WallCut and OpeningSideName; we are storing both of them because
   * wrapping settings are stored on the opening, and not on the WallOpeningSide.
   * once wrapping settings are stored on the WallOpeningSide, this can change.
   */
  const openingSidesLeft: {
    opening: GeneratedDoorPlacement | GeneratedWindowPlacement | WallCut;
    side: OpeningSide;
    sideName: OpeningSideName;
  }[] = [];
  const openingSidesRight: {
    opening: GeneratedDoorPlacement | GeneratedWindowPlacement | WallCut;
    side: OpeningSide;
    sideName: OpeningSideName;
  }[] = [];
  for (const opening of iterableSolidWallSideOpenings(leftSide)) {
    openingSidesLeft.push({ opening, side: wallOpeningSide(ensureDefined(opening.opening), FRONT), sideName: FRONT });
    openingSidesRight.push({ opening, side: wallOpeningSide(ensureDefined(opening.opening), BACK), sideName: BACK });
  }
  for (const opening of iterableSolidWallSideOpenings(rightSide)) {
    openingSidesRight.push({ opening, side: wallOpeningSide(ensureDefined(opening.opening), FRONT), sideName: FRONT });
    openingSidesLeft.push({ opening, side: wallOpeningSide(ensureDefined(opening.opening), BACK), sideName: BACK });
  }

  // for each finish layer union all opening sides and save the union
  for (const finishMass of leftSide.finishes) {
    const openingData = gatherOpeningDataFromMassOpenings(finishMass, openingSidesLeft);
    unionMassOpenings(finishMass, openingData);
  }
  for (const finishMass of rightSide.finishes) {
    const openingData = gatherOpeningDataFromMassOpenings(finishMass, openingSidesRight);
    unionMassOpenings(finishMass, openingData);
  }

  /**
   * Add hole faces from openings. these will be cut be the openings
   * First we cut the opening into existing faces, then we add the holeFaces into that opening.
   * this is to prevent openings from cutting their own hole faces.
   *
   * @NOTE - the order of these openings is important, but arbitrary.
   * Because we cut holes and then add faces, the hole faces added by an opening will not
   * be cut by previous hole in the event that they overlap. This means that we don't really cut the hole faces properly.
   * In the meantime, we need to maintain this cutting order to keep baselines from changing.
   *
   */

  for (const opening of iterableSolidWallSideOpenings(leftSide)) {
    // @TODO(Andrew): for OPEN OPENINGS only, iterate over all substrate masses and generate side finish faces
    for (const finishMass of leftSide.finishes) {
      const wallOpeningFinishWrapDistance = getWallSideFinishOpeningWrapDistance(mass, finishMass, opening, BACK);
      const holeFinishFaces = solidWallMakeOpeningHoleFinishFaces({
        wallSideFinishMass: finishMass,
        wallOpeningFinishWrapDistance,
        wallOpening: opening,
        wallOpeningIsFront: true,
      });
      mergeWallFaces(wallFaces, wallFacesOpposite(holeFinishFaces));
    }
    for (const finishMass of rightSide.finishes) {
      const wallOpeningFinishWrapDistance = getWallSideFinishOpeningWrapDistance(mass, finishMass, opening, FRONT);
      const holeFinishFaces = solidWallMakeOpeningHoleFinishFaces({
        wallSideFinishMass: finishMass,
        wallOpeningFinishWrapDistance,
        wallOpening: opening,
        wallOpeningIsFront: false,
      });
      mergeWallFaces(wallFaces, holeFinishFaces);
    }
  }
  for (const opening of iterableSolidWallSideOpenings(rightSide)) {
    for (const finishMass of leftSide.finishes) {
      const wallOpeningFinishWrapDistance = getWallSideFinishOpeningWrapDistance(mass, finishMass, opening, BACK);
      const holeFinishFaces = solidWallMakeOpeningHoleFinishFaces({
        wallSideFinishMass: finishMass,
        wallOpeningFinishWrapDistance,
        wallOpening: opening,
        wallOpeningIsFront: false,
      });
      mergeWallFaces(wallFaces, wallFacesOpposite(holeFinishFaces));
    }
    for (const finishMass of rightSide.finishes) {
      const wallOpeningFinishWrapDistance = getWallSideFinishOpeningWrapDistance(mass, finishMass, opening, FRONT);
      const holeFinishFaces = solidWallMakeOpeningHoleFinishFaces({
        wallSideFinishMass: finishMass,
        wallOpeningFinishWrapDistance,
        wallOpening: opening,
        wallOpeningIsFront: true,
      });
      mergeWallFaces(wallFaces, holeFinishFaces);
    }
  }

  /**
   * Cut holes into each finish mass wallFaces individually.
   */

  // side finish mass cuts are always the right side of the mass
  const isLeft = false;

  for (const leftFinishMass of leftSide.finishes) {
    let leftFinishMassFaces = wallFacesNew();
    mergeWallFaces(leftFinishMassFaces, wallFacesFromWallMass(leftFinishMass), {
      rightFaces: true, // even though this is the left side, we want the right faces
      startFaces: true,
      endFaces: true,
      topFaces: leftFinishMass.section.topOffset >= mass.section.topOffset,
    });

    const leftOutlineFaces = wallFacesOpposite(wallFacesFromWallMass(leftFinishMass));
    mergeWallFaces(wallOutlineFaces, leftOutlineFaces, { leftFaces: true });

    wallFacesCutWallSideOpenings(leftFinishMassFaces, leftFinishMass, leftSide, {
      isFront: true,
      isLeft,
    });

    /**
     * @NOTE - finish side masses follow the planar graph orientation, so
     * leftFinishMass.rightFaces = wallFaces.leftFaces.
     *
     * We need to flip these faces
     */
    leftFinishMassFaces = wallFacesOpposite(leftFinishMassFaces);

    mergeWallFaces(wallFaces, leftFinishMassFaces, {
      leftFaces: true,
      startFaces: true,
      endFaces: true,
      topFaces: leftFinishMass.section.topOffset >= mass.section.topOffset,
    });
  }

  for (const rightFinishMass of rightSide.finishes) {
    const rightFinishMassFaces = wallFacesNew();
    mergeWallFaces(rightFinishMassFaces, wallFacesFromWallMass(rightFinishMass), {
      rightFaces: true,
      startFaces: true,
      endFaces: true,
      topFaces: rightFinishMass.section.topOffset >= mass.section.topOffset,
    });

    const rightOutlineFaces = wallFacesFromWallMass(rightFinishMass);
    mergeWallFaces(wallOutlineFaces, rightOutlineFaces, { rightFaces: true });

    wallFacesCutWallSideOpenings(rightFinishMassFaces, rightFinishMass, leftSide, {
      isFront: false,
      isLeft,
    });

    mergeWallFaces(wallFaces, rightFinishMassFaces, {
      rightFaces: true,
      startFaces: true,
      endFaces: true,
      topFaces: rightFinishMass.section.topOffset >= mass.section.topOffset,
    });
  }

  wallFacesAddFinishCaps({
    wallFaces,
    leftSide,
    rightSide,
    isLeftSide: true,
  });

  wallFacesAddFinishCaps({
    wallFaces,
    leftSide,
    rightSide,
    isLeftSide: false,
  });

  return { wallFaces, wallOutlineFaces };
}

/**
 * Core cross section faces are a purely visual output. They patch
 * holes in the top and bottom of walls where we don't have a true material
 * assignment because they represent the core of the wall.
 *
 * This function will likely be updated as more information is created in the
 * wallAssembly regarding the makeup of the wall CORE
 */
function wallFacesAddCoreFaces(
  wallFaces: WallFaces,
  mass: WallMass,
  leftSide: SolidWallSide,
  rightSide: SolidWallSide,
) {
  /**
   * furring walls have a foundation compoment inside the substrate layer,
   * which means that the current bottomFace and topFace should be taken from the
   * WallLayerType.STRUCTURE layer
   */
  let wallLayerType = WallLayerType.SUBSTRATE;
  if (mass.path.wallDescriptor.isFurring) {
    wallLayerType = WallLayerType.STRUCTURE;
  }
  const coreCrossSectionLayers = wallMassLayers(mass, wallLayerType, WallLOD.L1)
    .filter(isSolidWallMass)
    .filter(({ section }) => {
      /**
       * Only include faces above the baseOffset, which represents
       * the bottom of framing
       */
      return section.topOffset > mass.path.baseOffset;
    });

  if (coreCrossSectionLayers.length) {
    const bottomCoreMass = coreCrossSectionLayers[0];
    const bottomCrossSectionFaces = wallFacesFromWallMass(bottomCoreMass);

    wallFacesCutWallSideOpenings(bottomCrossSectionFaces, bottomCoreMass, leftSide, {
      isFront: false,
      isLeft: true,
    });
    wallFacesCutWallSideOpenings(bottomCrossSectionFaces, bottomCoreMass, rightSide, {
      isFront: false,
      isLeft: false,
    });

    mergeWallFaces(wallFaces, bottomCrossSectionFaces, { bottomFaces: true });

    const topCoreMass = coreCrossSectionLayers[coreCrossSectionLayers.length - 1];

    // single-core-layer walls have topCoreMass === bottomCoreMass, so the cut
    // pipeline above already produced the identical top faces
    if (topCoreMass === bottomCoreMass) {
      mergeWallFaces(wallFaces, bottomCrossSectionFaces, { topFaces: true });
    } else {
      const topCrossSectionFaces = wallFacesFromWallMass(topCoreMass);

      wallFacesCutWallSideOpenings(topCrossSectionFaces, topCoreMass, leftSide, {
        isFront: false,
        isLeft: true,
      });
      wallFacesCutWallSideOpenings(topCrossSectionFaces, topCoreMass, rightSide, {
        isFront: false,
        isLeft: false,
      });

      mergeWallFaces(wallFaces, topCrossSectionFaces, { topFaces: true });
    }
  }

  /**
   * WALLSV2 - walls from walls v2 inject finishes into the core. If there are finishes present,
   * we need to extract the core faces and add them to the cut list for this wall
   *
   * @TODO - we may need to consider cutting the core unconditionally in the future,
   * but for now we only use these faces for finish rendering.
   */

  const coreHasFinishLayers = mass.wallAssembly.structuralLayers.some(isFinishLayer);
  if (!coreHasFinishLayers) {
    return;
  }

  const openingSidesRight: {
    opening: GeneratedDoorPlacement | GeneratedWindowPlacement | WallCut;
    sideName: OpeningSideName;
  }[] = [];

  for (const opening of iterableSolidWallSideOpenings(leftSide)) {
    openingSidesRight.push({ opening, sideName: BACK });
  }

  for (const opening of iterableSolidWallSideOpenings(rightSide)) {
    openingSidesRight.push({ opening, sideName: FRONT });
  }

  const coreFinishLayers = wallMassLayers(mass, WallLayerType.STRUCTURE, WallLOD.L2).filter(isSolidWallMass);
  if (coreFinishLayers.length) {
    // add all side faces from the core layers
    for (const coreFaceLayer of coreFinishLayers) {
      const coreFaceLayerAssembly = coreFaceLayer.wallAssembly;
      const coreFaceLayerHasFinish = coreFaceLayerAssembly.structuralLayers.some(isFinishLayer);
      if (!coreFaceLayerHasFinish) {
        // this mass does not need to be cut.
        // Remove this if we decide to cut the core unconditionally.
        continue;
      }

      const coreCrossSectionFaces = wallFacesFromWallMass(coreFaceLayer);
      const openingData = gatherOpeningDataFromMassOpenings(coreFaceLayer, openingSidesRight);
      unionMassOpenings(coreFaceLayer, openingData);

      wallFacesCutWallSideOpenings(coreCrossSectionFaces, coreFaceLayer, rightSide, {
        isFront: false,
        isLeft: false,
      });

      mergeWallFaces(wallFaces, coreCrossSectionFaces, {
        leftFaces: true,
        rightFaces: true,
        startFaces: true,
        endFaces: true,
        topFaces: true,
        bottomFaces: true,
      });
    }
  }
}

function wallFacesCutWallSideOpenings(
  wallFaces: WallFaces,
  wallFacesSourceMass: SolidWallMass, // source mass for face loop positioning
  wallSide: SolidWallSide,
  wallSideContext: WallSideContext,
) {
  const {
    id: layerId,
    rightFace: { plane: hostPlane },
  } = wallFacesSourceMass;
  const { isFront } = wallSideContext;
  const side = isFront ? FRONT : BACK;

  const { opening2ById, openingById, overlapsHostById } = unionedWallSideOpenings(
    wallFacesSourceMass,
    wallSide,
    wallSideContext,
  );

  /**
   * For reasons I don't yet understand, the input to this function
   * often receives wall faces with many disjoint coplanar faces rather
   * than a single face. This means the code is much slower than it
   * would otherwise be as every face needs to be clipped. Instead, we
   * first merge the coplanar faces, which commonly results in a single face.
   * But, this is non-ideal as we shouldn't have these be disjoint in the first
   * place.
   */
  if (wallFaces.leftFaces.length > 1 && faceTagsEqual(wallFaces.leftFaces)) {
    wallFaces.leftFaces = mergeNonAdjacentCoplanarFaces(wallFaces.leftFaces);
  }

  if (wallFaces.rightFaces.length > 1 && faceTagsEqual(wallFaces.rightFaces)) {
    wallFaces.rightFaces = mergeNonAdjacentCoplanarFaces(wallFaces.rightFaces);
  }

  for (const id of Object.keys(overlapsHostById)) {
    if (!overlapsHostById[id]) {
      continue;
    }

    const { opening } = openingById[id];
    if (!opening) {
      continue;
    }

    const cutLoops = wallOpeningSideLayer(opening, side, layerId).union?.lines ?? [opening2ById[id]];
    performWallCut(wallFaces, faceNew(cutLoops, hostPlane));
  }
}

function faceTagsEqual(faces: Face[]) {
  const tags = faces[0].tags;

  if (!tags) {
    return false;
  }

  for (let i = 1; i < faces.length; i++) {
    if (!shallowCompare(faces[i].tags, tags)) {
      return false;
    }
  }

  return true;
}

const CAP_FINISH_FACE_TAG: BuildingGeomTag = {
  type: "OPERATION",
  operation: "CAP_FINISH_FACE",
};

function wallFacesAddFinishCaps({
  wallFaces,
  leftSide,
  rightSide,
  isLeftSide,
}: {
  wallFaces: WallFaces;
  leftSide: SolidWallSide;
  rightSide: SolidWallSide;
  isLeftSide: boolean;
}) {
  const wallSide = isLeftSide ? leftSide : rightSide;

  for (const finishSideMass of wallSide.finishes) {
    const { section: wallSection, floorElevation } = finishSideMass;
    const subMassTag = getSubMassParentTag(finishSideMass.leftFace);

    const topCutPlane: LevelInches = levelInchesNew(wallSection.topOffset - DIST_EPSILON);
    const topCrossSections = wallSideFinishMassCrossSection(leftSide, rightSide, finishSideMass, topCutPlane);
    const topPlane = planeNewXY(0, 0, floorElevation + wallSection.topOffset);

    for (const finishes of topCrossSections) {
      const { layer, crossSection, finishId, thickness } = finishes;
      if (!isFinishLayer(layer)) {
        continue;
      }
      if (thickness < DIST_EPSILON) {
        continue;
      }

      const tags: BuildingGeomTag[] = [
        {
          type: "PARENT",
          parentId: finishId,
          parentType: "WALL_LAYER",
        },
        TAG_POSITION_TOP,
        CAP_FINISH_FACE_TAG,
      ];
      if (subMassTag) {
        tags.push(subMassTag);
      }

      for (const s of crossSection) {
        if (polyLine2Area(s) < ZERO_EPSILON) {
          continue;
        }
        const pl = polyLine2SetCW(polyLine2Copy(s));
        const face = faceNew([pl], topPlane);
        wallFaces.topFaces.push(tagFace(face, tags));
      }
    }

    const bottomCutPlane = levelInchesNew(wallSection.bottomOffset + DIST_EPSILON);
    const bottomCrossSections = wallSideFinishMassCrossSection(leftSide, rightSide, finishSideMass, bottomCutPlane);
    const bottomPlane = planeNewXY(0, 0, floorElevation + wallSection.bottomOffset);

    for (const finishes of bottomCrossSections) {
      const { layer, crossSection, finishId, thickness } = finishes;
      if (!isFinishLayer(layer)) {
        continue;
      }
      if (thickness < DIST_EPSILON) {
        continue;
      }

      const tags: BuildingGeomTag[] = [
        {
          type: "PARENT",
          parentId: finishId,
          parentType: "WALL_LAYER",
        },
        TAG_POSITION_BOTTOM,
        CAP_FINISH_FACE_TAG,
      ];
      if (subMassTag) {
        tags.push(subMassTag);
      }

      for (const s of crossSection) {
        if (polyLine2Area(s) < ZERO_EPSILON) {
          continue;
        }
        const pl = polyLine2SetCW(polyLine2Copy(s));
        const face = faceNew([pl], planeCopy(bottomPlane));
        wallFaces.bottomFaces.push(tagFace(faceReverse(face), tags));
      }
    }
  }
}

function getWallSideFinishOpeningWrapDistance(
  wallMass: WallMass,
  wallSideFinishMass: SolidWallMass,
  wallOpening: GeneratedWindowPlacement | GeneratedDoorPlacement | WallCut,
  wallSide: OpeningSideName,
) {
  const { opening, wrapping } = wallOpening;
  if (!opening || opening.hasJamb) {
    // jamb boards override any wrapping
    return 0;
  }
  const { section } = wallSideFinishMass;
  const { topOffset, bottomOffset } = section;
  const finishCutPlane = (bottomOffset + topOffset) / 2;
  const substrateMass = wallMassCrossSectionLayer(wallMass, WallLayerType.SUBSTRATE, WallLOD.L2, finishCutPlane);
  if (!substrateMass || substrateMass.wallType === WallType.VOID) {
    // nothing to wrap here
    return 0;
  }

  const mode = wrapping?.wrappingMode || OpeningWrappingModeEnum.NONE;
  let distance: number;
  switch (mode) {
    case OpeningWrappingModeEnum.CENTER_OF_CORE:
      distance = substrateMass.thickness / 2;
      break;
    case OpeningWrappingModeEnum.INTERIOR_CORE_FACE:
      distance = 0;
      break;
    case OpeningWrappingModeEnum.EXTERIOR_CORE_FACE:
    case OpeningWrappingModeEnum.NONE:
    default:
      distance = substrateMass.thickness;
      break;
  }
  return wallSide === BACK ? distance : substrateMass.thickness - distance;
}
